import { Role } from '@/lib/auth/roles/role.enum';
import { useUserHasRole } from '@/lib/auth/useUserHasRoles';
import { appData } from '@/lib/data';
import { getQueryString, pushParamsToUrl } from '@/lib/util';
import { useGetElearningCategoriesV2Query } from '@/modules/services';
import { ElaCategoryType } from '@/modules/types/elearningCategories';
import MyLibraryRoutes from '@/pages/MyLibrary/MyLibraryRoutes';
import {
  Spinner,
  Row,
  Col,
  Search,
  DataTable,
  Toggle,
  Button,
  Text,
  Heading,
  EditOutlinedIcon,
  Badge,
  Popover,
} from 'elmo-elements';
import React, { useEffect, useState } from 'react';
import { generatePath, Link, useHistory, useLocation } from 'react-router-dom';
import './index.scss';
import { ContentManagerCategoryRoutes } from '../ContentManagerRoutes';
import PopOverDisplay from './CategoriesPopOver';
import CategoryModal from './CategoryModal';

const getColumns = (showStatus: boolean) => {
  // Base columns that are always present
  const baseColumns = [
    {
      title: 'Title',
      sortable: true,
      sortKey: 'name',
    },
    {
      title: 'Modules',
      sortable: false,
      sortKey: 'modules',
    },
  ];

  // Conditionally add the Status column
  if (showStatus) {
    baseColumns.push({
      title: 'Status',
      sortable: false,
      sortKey: 'status',
    });
  }

  // Always add the empty actions column
  baseColumns.push({
    title: '',
    sortable: false,
    sortKey: '',
  });

  return baseColumns;
};

const CategoriesManagerComponent = (): React.ReactElement => {
  const location = useLocation();
  const history = useHistory();

  const currentQueryString = getQueryString(
    location.search,
    appData.pagination.defaultParams,
  );

  const hasAdminRole = useUserHasRole([Role.Admin, Role.Super, Role.Migration]);
  const limit = currentQueryString?.limit || 10;
  const [searchKeyword, setSearchKeyword] = useState<string>(
    currentQueryString?.search || '',
  );
  const [currentPage, setCurrentPage] = useState(currentQueryString.page || 1);
  const [order, setOrder] = useState<'asc' | 'desc'>(
    currentQueryString.order?.toLowerCase() || 'asc',
  );
  const [sortColumn, setSortColumn] = useState(0);
  const [showStatus, setShowStatus] = useState<boolean>(
    !!currentQueryString.showDisabled || false,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categoryData, setCategoryData] = useState<ElaCategoryType | undefined>(
    undefined,
  );

  const { data: categoriesData, isLoading: isCategoriesDataLoading } =
    useGetElearningCategoriesV2Query(currentQueryString, {
      skip: !hasAdminRole,
    });

  useEffect(() => {
    if (currentQueryString.showDisabled) {
      setShowStatus(currentQueryString.showDisabled);
    }
  }, [location]);

  const handleSearch = (keyword: string) => {
    pushParamsToUrl(
      {
        search: keyword,
        page: 1,
      },
      history,
    );
  };

  const handleLimitChange = (newSize: number) => {
    pushParamsToUrl(
      {
        limit: newSize,
      },
      history,
    );
  };

  const handleSearchChange = (searchValue: string) => {
    setSearchKeyword(searchValue);

    if (searchValue === '') handleSearch('');
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    pushParamsToUrl({ page: pageNumber }, history);
  };

  const handleColumnSort = (columnIndex: number) => {
    const sortKey = getColumns(showStatus)[columnIndex].sortable
      ? getColumns(showStatus)[columnIndex].sortKey
      : '';
    if (!sortKey) return;

    const newOrder =
      order === 'asc' && sortColumn === columnIndex ? 'desc' : 'asc';

    setOrder(newOrder);
    setSortColumn(columnIndex);

    pushParamsToUrl(
      {
        sort: sortKey,
        order: newOrder.toUpperCase(),
        page: 1,
      },
      history,
    );
  };

  const handleToggleDisplay = (isDisabled: boolean) => {
    setShowStatus(isDisabled);

    if (!isDisabled) {
      const params = new URLSearchParams(location.search);
      params.delete('showDisabled');

      history.replace({
        search: params.toString(),
      });
      return;
    }

    pushParamsToUrl(
      {
        showDisabled: isDisabled,
        page: 1,
      },
      history,
    );
  };

  const handleEdit = (data: ElaCategoryType) => {
    setCategoryData(data);
    setIsModalOpen(true);
  };

  const renderPopOver = (category: ElaCategoryType) =>
    category.modules ? (
      <DataTable.Td key="pop-over-modules">
        <Popover className="pop-over-modules">
          <Popover.Target>
            <Link
              to={ContentManagerCategoryRoutes.path.replace(
                ':categoryId',
                category.id,
              )}
            >
              <Text>{category.modules}</Text>
            </Link>
          </Popover.Target>
          <Popover.Content>
            <PopOverDisplay categoryId={category.id} />
          </Popover.Content>
        </Popover>
      </DataTable.Td>
    ) : (
      <DataTable.Td key="modules">
        <Link
          to={ContentManagerCategoryRoutes.path.replace(
            ':categoryId',
            category.id,
          )}
        >
          <Text>{category.modules}</Text>
        </Link>
      </DataTable.Td>
    );

  if (hasAdminRole !== undefined && !hasAdminRole) {
    history.replace(generatePath(MyLibraryRoutes.path));
  }
  if (isCategoriesDataLoading || categoriesData === undefined)
    return <Spinner />;

  return (
    <div id="Categories" className="content-manager">
      <CategoryModal
        categoryData={categoryData}
        isModalOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setCategoryData(undefined);
        }}
        onSubmit={(isSuccess) => {
          setIsModalOpen(!isSuccess);
          setCategoryData(undefined);
        }}
      />
      <Row className="categories-header categories-label-row">
        <Col span={8}>
          <Heading size="lg" htmlTag="h2" type="heading">
            Categories
          </Heading>
        </Col>
        <Col span={4} className="create-category-btn">
          <Button onClick={() => setIsModalOpen(true)}>Create new</Button>
        </Col>
      </Row>
      <Row className="categories-header categories-label-row">
        <Col span={6}>
          <span className="filter-toggle">
            <Toggle
              isOn={showStatus}
              onChange={(e) => {
                handleToggleDisplay(e.target.checked);
              }}
              onClick={() => {
                /** this is intentional, as toggle props still require onClick even though it's deprecated. */
              }}
            />
            <Text>Show disabled categories</Text>
          </span>
        </Col>
        <Col span={6} className="search">
          <Search
            value={searchKeyword}
            isVisible
            onChange={(value) => handleSearchChange(value)}
            onSubmit={() => handleSearch(searchKeyword)}
            isSubmitEnabledAlways={true}
          />
        </Col>
      </Row>
      <DataTable
        className="content-manager-categories-table"
        isLoading={isCategoriesDataLoading}
      >
        <DataTable.Header
          columns={getColumns(showStatus)}
          onSort={handleColumnSort}
          sortColumn={sortColumn}
          sortDirection={order}
        />
        <DataTable.Body>
          {categoriesData?.data?.map((category: ElaCategoryType) => {
            // This is intentional to make conditional rendering work for column 3 status
            const cells = [
              <DataTable.Td key="title">
                <Link
                  to={ContentManagerCategoryRoutes.path.replace(
                    ':categoryId',
                    category.id,
                  )}
                >
                  {category.name}
                </Link>
              </DataTable.Td>,
              renderPopOver(category),
            ];

            // Status cell (conditionally included)
            if (showStatus) {
              cells.push(
                <DataTable.Td key="status">
                  <Badge type={category.isEnabled ? 'success' : 'grey'}>
                    {category.isEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </DataTable.Td>,
              );
            }

            cells.push(
              <DataTable.Td key="actions">
                <span className="action-buttons">
                  <Button onClick={() => handleEdit(category)}>
                    <EditOutlinedIcon />
                  </Button>
                </span>
              </DataTable.Td>,
            );

            // Return the row with all cells
            return <DataTable.Tr key={category.id}>{cells}</DataTable.Tr>;
          })}
        </DataTable.Body>
        <DataTable.Pagination
          pageSize={categoriesData?.meta.limit ?? limit}
          pageSizeOptions={[10, 20]}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onPageSizeChange={handleLimitChange}
          totalResults={categoriesData?.meta.itemCount ?? 0}
        />
      </DataTable>
    </div>
  );
};

export default CategoriesManagerComponent;
